// GoogleSignInButton renders Google's own "Sign in / Sign up with Google" button
// (Google Identity Services, GIS) and hands the ID-token credential to the caller.
//
// How GIS behaves, and what that means here:
//
//   - The button is an iframe Google draws. Clicking it opens Google's account
//     chooser in a popup (ux_mode "popup") and the credential arrives through the
//     callback given to initialize(). There is NO callback when the popup is
//     closed, blocked or fails inside Google, so this component shows no busy
//     state between the click and the credential: the only busy state is the
//     caller's POST. `onClick` (GIS click_listener) is telemetry; it cannot cancel
//     the sign-in.
//   - initialize() is global and keeps the callback it was given. Calling it again
//     on remount is fine (the latest call wins), and the callback forwards through
//     `latestRef`, so the handler of the CURRENT render runs, not a stale closure.
//   - One Tap-only options (context, itp_support, cancel_on_tap_outside,
//     use_fedcm_for_button) are omitted on purpose: this component never calls
//     prompt(), so they would change nothing and only invite confusion.
//   - GIS draws the iframe at a fixed pixel width (200 to 400). The container is
//     measured so a narrow phone column gets a button that fits; jsdom measures 0
//     and falls back to the `width` prop.
//   - GIS has no font-size or weight option, and `size: "large"` is its biggest.
//     `scale` (above 1) enlarges the whole drawn button with a CSS transform.
//     Google's branding guidelines allow scaling the button as long as the
//     aspect ratio is kept, which a uniform scale does. GIS is asked for the
//     measured width divided by the scale, so the scaled button still fills the
//     column, and the wrapper reserves the scaled height because a transform
//     takes no layout space. Values of 1 or less are ignored. A scaled button
//     still opens Google's popup (checked in Chrome, 2026-11-03).
//   - Android / iOS in-app webviews are not supported by Google for sign-in: the
//     callers hide this component there (shared/utils/embeddedBrowser.js).
//
// `gated`: the caller's form may need consent first (terms, state). While gated a
// transparent button overlays the iframe and takes the click (`onGatedClick`, so
// the form can show its validation), and the GIS container is made `inert` so
// keyboard focus cannot reach the iframe. The overlay is an affordance, not the
// guarantee: the caller re-validates when the credential arrives.
//
// Nothing here logs. The credential is a bearer token for the user's Google
// identity; it goes to `onCredential` and nowhere else.

import { useEffect, useRef, useState } from "react";

import { loadGoogleIdentity } from "~/shared/utils/googleIdentity";

export const GSI_BUTTON_MIN_WIDTH = 200;
export const GSI_BUTTON_MAX_WIDTH = 400;
export const GSI_BUTTON_DEFAULT_WIDTH = 300;
// The height GIS draws a `size: "large"` button (its iframe is 44px with -2px
// vertical margins). The wrapper reserves it so the page doesn't jump when the
// iframe arrives.
export const GSI_LARGE_BUTTON_HEIGHT = 40;

// The `reason` passed to onUnavailable when no client id is configured.
export const UNAVAILABLE_NO_CLIENT_ID = "client_id_missing";

// clampGoogleButtonWidth keeps a pixel width inside the range GIS accepts.
export const clampGoogleButtonWidth = (px) => {
  const n = Math.floor(Number(px));
  if (!Number.isFinite(n)) return GSI_BUTTON_DEFAULT_WIDTH;
  return Math.min(GSI_BUTTON_MAX_WIDTH, Math.max(GSI_BUTTON_MIN_WIDTH, n));
};

export default function GoogleSignInButton({
  clientID,
  text,
  width = GSI_BUTTON_DEFAULT_WIDTH,
  scale = 1,
  gated = false,
  gatedLabel,
  onGatedClick,
  onCredential,
  onClick,
  onShown,
  onUnavailable,
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const latestRef = useRef({});
  const buttonScale = Number(scale) > 1 ? Number(scale) : 1;
  latestRef.current = {
    onCredential,
    onClick,
    onShown,
    onUnavailable,
    onGatedClick,
  };
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    const giveUp = (reason) => {
      if (cancelled) return;
      setUnavailable(true);
      latestRef.current.onUnavailable?.(reason);
    };

    if (!clientID) {
      giveUp(UNAVAILABLE_NO_CLIENT_ID);
      return undefined;
    }
    if (!container) return undefined;

    loadGoogleIdentity()
      .then((api) => {
        if (cancelled) return;
        api.initialize({
          client_id: clientID,
          ux_mode: "popup",
          callback: (response) =>
            latestRef.current.onCredential?.({
              credential: response.credential,
              selectBy: response.select_by,
            }),
        });
        container.replaceChildren();
        // The wrapper, not the container: the container carries the scale
        // transform, and getBoundingClientRect includes transforms.
        const measured = wrapperRef.current?.getBoundingClientRect().width;
        api.renderButton(container, {
          type: "standard",
          theme: "outline",
          size: "large",
          text,
          shape: "pill",
          logo_alignment: "left",
          width: String(
            clampGoogleButtonWidth((measured || width) / buttonScale),
          ),
          click_listener: () => latestRef.current.onClick?.(),
        });
        latestRef.current.onShown?.();
      })
      .catch((err) => giveUp(err?.message || "gsi_unavailable"));

    return () => {
      cancelled = true;
      container.replaceChildren();
    };
  }, [clientID, text, width, buttonScale]);

  // React 17 has no `inert` prop, so toggle the attribute by hand.
  useEffect(() => {
    containerRef.current?.toggleAttribute("inert", Boolean(gated));
  }, [gated, unavailable]);

  if (unavailable) return null;

  return (
    <div
      ref={wrapperRef}
      data-testid="google-signin"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: clampGoogleButtonWidth(width),
        minHeight: Math.round(GSI_LARGE_BUTTON_HEIGHT * buttonScale),
        margin: "0 auto",
      }}
    >
      <div
        ref={containerRef}
        data-testid="google-signin-container"
        style={{
          display: "flex",
          justifyContent: "center",
          ...(buttonScale > 1 && {
            transform: `scale(${buttonScale})`,
            transformOrigin: "center top",
          }),
        }}
      />
      {gated && (
        <button
          type="button"
          aria-label={gatedLabel}
          data-testid="google-signin-gate"
          onClick={() => latestRef.current.onGatedClick?.()}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: "100%",
            height: "100%",
            margin: 0,
            padding: 0,
            border: 0,
            background: "transparent",
            cursor: "pointer",
          }}
        />
      )}
    </div>
  );
}
